class V3BenefitsTabs extends HTMLElement {
  constructor() {
    super()
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'))
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'))
    this.stack = this.querySelector(".benefits-panels-stack")
    this.prevBtn = this.querySelector("[data-arrow-prev]")
    this.nextBtn = this.querySelector("[data-arrow-next]")

    if (this.stack) {
      const track = document.createElement("div")
      track.className = "benefits-panels-track"
      while (this.stack.firstChild) {
        track.appendChild(this.stack.firstChild)
      }
      this.stack.appendChild(track)
      this.track = track
    }

    this.panels.forEach((panel) => {
      const cardTrack = document.createElement("div")
      cardTrack.className = "benefits-cards-track"
      while (panel.firstChild) {
        cardTrack.appendChild(panel.firstChild)
      }
      panel.appendChild(cardTrack)
    })

    const initialTab = this.tabs.findIndex(
      (t) => t.getAttribute("aria-selected") === "true"
    )
    this.activeTabIndex = initialTab === -1 ? 0 : initialTab
    this.activeCardIndex = 0

    this.tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => this.goTo(i, 0))
      tab.addEventListener("keydown", (e) => this.onTabKeyDown(e, i))
    })

    this.panels.forEach((panel, panelIndex) => {
      const cards = panel.querySelectorAll("[data-benefit-card]")
      cards.forEach((card, cardIndex) => {
        card.addEventListener("click", () =>
          this.goTo(panelIndex, cardIndex)
        )
      })
    })

    if (this.prevBtn) this.prevBtn.addEventListener("click", () => this.step(-1))
    if (this.nextBtn) this.nextBtn.addEventListener("click", () => this.step(1))

    this.addSwipeHandlers()

    this.render()
  }

  addSwipeHandlers() {
    if (!this.stack) return
    let startX = null
    let startY = null
    const threshold = 40

    this.stack.addEventListener(
      "touchstart",
      (e) => {
        const t = e.touches[0]
        startX = t.clientX
        startY = t.clientY
      },
      { passive: true }
    )

    this.stack.addEventListener(
      "touchend",
      (e) => {
        if (startX === null) return
        const t = e.changedTouches[0]
        const dx = t.clientX - startX
        const dy = t.clientY - startY
        startX = null
        startY = null
        if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
          this.step(dx < 0 ? 1 : -1)
        }
      },
      { passive: true }
    )
  }

  get cardsInActiveTab() {
    return this.panels[this.activeTabIndex].querySelectorAll(
      "[data-benefit-card]"
    )
  }

  step(direction) {
    const cardCount = this.cardsInActiveTab.length || 1
    let nextCard = this.activeCardIndex + direction
    let nextTab = this.activeTabIndex

    if (nextCard >= cardCount) {
      nextTab = (nextTab + 1) % this.tabs.length
      nextCard = 0
    } else if (nextCard < 0) {
      nextTab = (nextTab - 1 + this.tabs.length) % this.tabs.length
      const prevPanelCards = this.panels[nextTab].querySelectorAll(
        "[data-benefit-card]"
      )
      nextCard = Math.max(0, prevPanelCards.length - 1)
    }

    this.goTo(nextTab, nextCard)
  }

  goTo(tabIndex, cardIndex, { focusTab = false } = {}) {
    this.activeTabIndex = tabIndex
    this.activeCardIndex = cardIndex
    this.render({ focusTab, scroll: true })
  }

  onTabKeyDown(e, index) {
    let nextIndex = null
    if (e.key === "ArrowRight")
      nextIndex = (index + 1) % this.tabs.length
    if (e.key === "ArrowLeft")
      nextIndex = (index - 1 + this.tabs.length) % this.tabs.length

    if (nextIndex !== null) {
      e.preventDefault()
      this.goTo(nextIndex, 0, { focusTab: true })
    }
  }

  render({ focusTab = false, scroll = false } = {}) {
    if (this.track) {
      this.track.style.setProperty(
        "--active-tab-index",
        String(this.activeTabIndex)
      )
    }

    this.tabs.forEach((tab, i) => {
      const isActive = i === this.activeTabIndex
      tab.setAttribute("aria-selected", isActive ? "true" : "false")
      tab.setAttribute("tabindex", isActive ? "0" : "-1")
    })

    this.panels.forEach((panel, i) => {
      const isActive = i === this.activeTabIndex
      panel.classList.toggle("is-active", isActive)
      panel.setAttribute("aria-hidden", isActive ? "false" : "true")

      const cards = panel.querySelectorAll("[data-benefit-card]")
      cards.forEach((card, cIdx) => {
        const cardActive = isActive && cIdx === this.activeCardIndex
        card.classList.toggle("is-active", cardActive)
        card.setAttribute("aria-hidden", cardActive ? "false" : "true")
      })

      const cardIndex = isActive ? this.activeCardIndex : 0
      panel.style.setProperty("--active-card-index", String(cardIndex))
    })

    const activeTab = this.tabs[this.activeTabIndex]

    if (scroll) {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches

      activeTab.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "center",
      })
    }

    if (focusTab) activeTab.focus()
  }
}

customElements.define("v3-benefits-tabs", V3BenefitsTabs)
